import { useCallback, useEffect, useRef } from "react";
import { type FieldPath, useFormContext, useWatch } from "react-hook-form";

import { isHttpError } from "../../../../../lib/query/errors";
import { isCompleteZip, useZipLookup } from "../api/useZipLookup";
import type { SignersInput } from "../Signers.schema";

/** Lookup state surfaced to the UI so it can show not-found / error feedback. */
export type ZipLookupStatus = "idle" | "loading" | "not-found" | "error";

interface UseZipAutofillParameters {
  zipPath: FieldPath<SignersInput>;
  cityPath: FieldPath<SignersInput>;
  statePath: FieldPath<SignersInput>;
  /** When false the hook skips querying and writing (e.g. mailing block while "Same as current" is on). Defaults to true. */
  enabled?: boolean;
}

interface ZipAutofillResult {
  status: ZipLookupStatus;
  /** True when the lookup failed (not-found or error) — drives the field's invalid (red-border) state. */
  invalid: boolean;
}

/**
 * Drives ZIP -> City/State autofill for any address block whose field paths are
 * provided. Once the ZIP is a complete 5 digits, looks up the address and — on a
 * user-driven change — fills City and State from the response. Never overrides
 * the hydrated address on initial load. The BFF returns a single city, so this
 * only sets values; it does not produce City dropdown options.
 *
 * City/State are cleared — not just left stale — whenever the current ZIP no
 * longer has a trustworthy match: editing the ZIP down to something incomplete
 * clears them immediately (reacting to the same `useWatch`'d value that drives
 * every other branch here, so this fires on every keystroke, not just blur),
 * and a resolved not-found/error lookup clears them as soon as it settles.
 *
 * When `enabled` is false the hook does not query and does not write — use this
 * to suppress autofill on a block that is mirrored / disabled externally.
 *
 * Returns a `status` so the field can surface a clear not-found / error state
 * instead of silently doing nothing.
 * @param {UseZipAutofillParameters} params - Which address fields to drive.
 * @returns {ZipAutofillResult} The current lookup status for this block.
 */
export function useZipAutofill({
  zipPath,
  cityPath,
  statePath,
  enabled = true,
}: UseZipAutofillParameters): ZipAutofillResult {
  const { control, setValue } = useFormContext<SignersInput>();
  const zip = useWatch({ control, name: zipPath }) as string | undefined;

  // The ZIP present at mount is the hydrated value; its city/state are already
  // hydrated, so only a subsequent (user-driven) ZIP change triggers autofill.
  const initialZipReference = useRef(zip);
  const isUserChangedZip = zip !== initialZipReference.current;

  // Gate the query on `enabled` AND a user-driven change: the hydrated ZIP
  // needs no lookup, so we avoid a wasted request whose result the effect below
  // would only discard. (Call the hook unconditionally for rules-of-hooks.)
  const { data, isError, error, isFetching } = useZipLookup(enabled && isUserChangedZip ? zip : undefined);

  const clearCityState = useCallback(() => {
    const options = { shouldDirty: true, shouldValidate: true } as const;
    setValue(cityPath, "", options);
    setValue(statePath, "", options);
  }, [cityPath, statePath, setValue]);

  useEffect(() => {
    if (!enabled || !isUserChangedZip) {
      return;
    }
    if (!isCompleteZip(zip)) {
      // An incomplete ZIP never triggers a lookup, so nothing else here would
      // ever clear a previously-resolved City/State for it.
      clearCityState();
      return;
    }
    if (data) {
      const options = { shouldDirty: true, shouldValidate: true } as const;
      if (data.city) {
        setValue(cityPath, data.city, options);
      }
      if (data.state) {
        setValue(statePath, data.state, options);
      }
      return;
    }
    // A settled (non-loading) failure — 404 not-found or a transient error —
    // means City/State can no longer be trusted, so clear them instead of
    // leaving the previous ZIP's values in place.
    if (isError) {
      clearCityState();
    }
  }, [enabled, zip, data, isError, isUserChangedZip, cityPath, statePath, setValue, clearCityState]);

  let status: ZipLookupStatus = "idle";
  if (enabled && isFetching) {
    status = "loading";
  } else if (enabled && isError) {
    // A 404 means the ZIP simply has no match; anything else is a transient
    // / network failure the user can retry.
    status = isHttpError(error, 404) ? "not-found" : "error";
  }

  return { status, invalid: status === "not-found" || status === "error" };
}
